import { useEffect, useState } from 'react';

import Layout from '../../components/Layout/Layout';
import { MaterialBioType, MaterialCategory, MaterialStatus, MaterialType, Usage } from '../../enum/Material';
import isAdminUser from '../../hoc/isAdminUser';
import './BackofficeMaterial.scss';
import { IBackofficeMaterialProps, IFCustomFile, IFCustomInfo, IFormikBOMUpdate } from './IBackofficeMaterial';
import { Field, Form, Formik, useFormikContext } from 'formik';
import Button from '../../../components/Button/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoint } from '..';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import { useTranslation } from 'react-i18next';
import { FormikImages } from './components/FormikImages';
import { FormikGeneralInfo } from './components/FormikGeneralInfo';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import Spinner from '../../../components/Spinner/Spinner';
import { IMaterialResponse } from '../../api/IApiResponse';
import { IMaterialRequest } from '../../api/IApiRequest';
import { FormikInput } from './components/FormikInput';
import { FormikPhysicalData } from './components/FormikPhysicalData';
import { materialTypeOptions } from '../../MACRA_1/components/InputForm/Materials/MaterialDropdowns/MaterialTypeDropdown';
import { toastSuccess, warning } from '../../../components/Alert/Alert';
import { conversionLabel } from '../../conversion';
import { selectProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { useAppSelector } from '../../../reduxService/hooks';

const BackOfficeMaterialUpdate = (props: IBackofficeMaterialProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();

    const [initialValues, setInitialValues] = useState<IFormikBOMUpdate>();
    const [material, setMaterial] = useState<IMaterialResponse>();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchMaterial = async () => {
            if (!!id) {
                const materialDetail = await BeMaccaferriStoreHelper.getMaterialWithAllLanguagesByIdAsync(parseInt(id));
                if (!!materialDetail) {
                    let images: IFCustomFile[] = [];
                    let infos: IFCustomInfo[] = [{ languageCodeISO: 'en', name: '', description: '' }];
                    if (!!materialDetail.data.images && materialDetail.data.images.length > 0) {
                        images = materialDetail.data.images.map(img => ({ ...img, imageUrl: img.url, actualFile: undefined }));
                    }
                    if (!!materialDetail.data.infos && materialDetail.data.infos.length > 0) {
                        infos = materialDetail.data.infos;
                    }
                    const { data } = materialDetail;
                    const { physicalData } = data;
                    setInitialValues({
                        usage: data.usage,
                        type: data.type,
                        category: data.category,
                        creationDate: data.creationDate,
                        updateDate: data.updateDate,
                        isDeleted: data.isDeleted,
                        isXties: !!data.isXties ? data.isXties : false,
                        isCastoro: !!data.isCastoro ? data.isCastoro : false,
                        isGeotextileFilter: !!data.isGeotextileFilter ? data.isGeotextileFilter : false,
                        status: data.status === MaterialStatus.Public,
                        color: data.color,
                        images,
                        roughness: 0,
                        allowShearStress: 0,
                        infos,
                        isAvailableStilling: data.isAvailableStilling ?? false,
                        isAvailableSoil: data.isAvailableSoil ?? false,
                        isAllowedChangeCheckbox: !!data.isAllowedChangeCheckbox ? data.isAllowedChangeCheckbox : false,
                        physicalData: !!physicalData && physicalData.length > 0 ? physicalData : [],
                    });
                    setMaterial(materialDetail.data);
                }
            }
        };
        fetchMaterial();
    }, []);
    const physicalDataCu = !!material && material.physicalData?.find(pd => !!pd.cu);

    return (
        <Layout>
            {initialValues ? (
                <Formik
                    initialValues={initialValues}
                    onSubmit={async values => {
                        setLoading(true);
                        try {
                            if (!!material) {
                                const result = await BeMaccaferriStoreHelper.updateMaterialAsync(values, material);
                                if (!!result.data && result.data) {
                                    toastSuccess(t('bo_material_toast_success_update'));
                                    navigate(endpoint.backoffice);
                                } else {
                                    warning(t('bo_material_toast_error_update'));
                                }
                            }
                        } catch (err) {
                            warning(t('bo_material_toast_error_update'));
                        } finally {
                            setLoading(false);
                        }
                    }}>
                    {({ handleSubmit, values, handleChange }) => {
                        return (
                            <Form onSubmit={handleSubmit} className='overflow-auto scroll-no-width h-100'>
                                <div className='d-flex align-items-center gap-4 m-2 dropdown-check'>
                                    <Dropdown
                                        title={t('bo_material_dd_typology')}
                                        name='type'
                                        className='d-flex align-items-center gap-2'
                                        value={values.type}
                                        options={materialTypeOptions()}
                                        onChange={handleChange}
                                    />
                                    <Checkbox title={t('bo_material_checkbox_status')} name='status' defaultChecked={values.status} onChange={handleChange} />
                                    {values.category !== MaterialCategory.MacMat && (
                                        <>
                                            <Checkbox
                                                title={t('bo_material_checkbox_isGeotextileFilter')}
                                                name='isGeotextileFilter'
                                                defaultChecked={values.isGeotextileFilter}
                                                onChange={handleChange}
                                            />
                                            {(values.category === MaterialCategory.Other || (values.category === MaterialCategory.Reno && !!physicalDataCu)) && (
                                                <Checkbox title={t('bo_material_checkbox_isXties')} name='isXties' defaultChecked={values.isXties} onChange={handleChange} />
                                            )}
                                            {(values.category === MaterialCategory.Other || values.category === MaterialCategory.Reno) && (
                                                <Checkbox title={t('bo_material_checkbox_isCastoro')} name='isCastoro' defaultChecked={values.isCastoro} onChange={handleChange} />
                                            )}
                                        </>
                                    )}
                                    <Checkbox
                                        title={t('bo_material_checkbox_isAvailableStillingBasin')}
                                        name='isAvailableStilling'
                                        defaultChecked={values.isAvailableStilling}
                                        onChange={handleChange}
                                    />
                                    <Checkbox title={t('bo_material_checkbox_isAvailableSoil')} name='isAvailableSoil' defaultChecked={values.isAvailableSoil} onChange={handleChange} />
                                    <Checkbox
                                        title={t('bo_material_checkbox_isAllowedChangeCheckbox')}
                                        name='isAllowedChangeCheckbox'
                                        defaultChecked={values.isAllowedChangeCheckbox}
                                        onChange={handleChange}
                                    />

                                    <Input
                                        type='color'
                                        title={t('bo_material_input_color')}
                                        name='color'
                                        className='d-flex align-items-center gap-2'
                                        classNameInput='colored-box'
                                        value={values.color}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className='d-flex justify-content-center gap-4'>
                                    <FormikGeneralInfo />
                                    <div className='w-25 d-flex flex-column gap-2'>
                                        <label className='py-3'>{t('bo_material_titles_pd')}</label>
                                        <SwitchComponentByCategory materialResponse={material} />
                                    </div>
                                    <FormikImages />
                                </div>
                                <div className='d-flex justify-content-end gap-3 m-5'>
                                    <Button
                                        type='button'
                                        classNameBtn='btn-color-inverted'
                                        onClick={() => {
                                            navigate(endpoint.backoffice);
                                        }}>
                                        {t('bo_material_cancel_btn')}
                                    </Button>
                                    {loading ? <Spinner /> : <Button type='submit'>{t('bo_material_save_btn')}</Button>}
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            ) : (
                <Spinner />
            )}
        </Layout>
    );
};
interface ISwitchComponentByCategory {
    materialResponse: IMaterialResponse | IMaterialRequest | undefined;
}
const SwitchComponentByCategory = (props: ISwitchComponentByCategory) => {
    const { materialResponse } = props;
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);

    if (!materialResponse) return <></>;
    switch (materialResponse.category) {
        case MaterialCategory.Gabion:
        case MaterialCategory.Reno:
            return <FormikPhysicalData />;
        case MaterialCategory.MacMat:
            return (
                <div className='inputPhysicalData'>
                    <FormikInput label={t('bo_material_input_minrf')} name='physicalData[0].minRf' type='number' />
                    <FormikInput label={t('bo_material_input_maxrf')} name='physicalData[0].maxRf' type='number' />
                    <FormikInput label={t('bo_material_input_mintime')} name='physicalData[0].minTime' type='number' />
                    <FormikInput label={t('bo_material_input_maxtime')} name='physicalData[0].maxTime' type='number' />
                    <FormikInput
                        label={t('bo_material_input_shearstress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].allowShearStress'
                        type='number'
                        step={0.01}
                    />
                    <FormikInput label={t('bo_material_input_roughness')} name='physicalData[0].roughness' type='number' step={0.0001} />
                    <FormikInput
                        label={t('bo_material_input_vegetatedAllowShearStress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].vegetatedAllowShearStress'
                        type='number'
                        step={0.01}
                    />
                    <FormikInput label={t('bo_material_input_vegetatedRoughness')} name='physicalData[0].vegetatedRoughness' type='number' step={0.01} />
                </div>
            );
        case MaterialCategory.Riprap:
            return (
                <div className='inputPhysicalData'>
                    <FormikInput
                        label={t('bo_material_input_thickness', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].thickness'
                        type='number'
                    />
                    <FormikInput
                        label={t('bo_material_input_rockD50', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].rockD50'
                        type='number'
                    />
                    <FormikInput
                        label={t('bo_material_input_shearstress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].allowShearStress'
                        type='number'
                        step={0.01}
                    />
                    <FormikInput label={t('bo_material_input_roughness')} name='physicalData[0].roughness' type='number' step={0.0001} />
                </div>
            );
        case MaterialCategory.Cohesive:
        case MaterialCategory.Other:
        default:
            return (
                <div className='inputPhysicalData'>
                    <FormikInput
                        label={t('bo_material_input_shearstress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                        name='physicalData[0].allowShearStress'
                        type='number'
                        step={0.01}
                    />
                    <FormikInput label={t('bo_material_input_roughness')} name='physicalData[0].roughness' type='number' step={0.0001} />
                </div>
            );
    }
};
export default isAdminUser(BackOfficeMaterialUpdate);
